import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";

import { authService } from "../services/authService";

interface AuthContextType {
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => void;
  getToken: () => string | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        let isCallbackHandled = false;

        // Handle logout callback - simple redirect to landing page like the working Angular app
        if (window.location.pathname.includes("logout/callback")) {
          authService.clearAllAuthState();

          // Clear URL hash and search params
          if (window.location.hash || window.location.search) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }

          setIsAuthenticated(false);
          setLoading(false);

          // Redirect to landing page (index.html equivalent) like the working Angular app
          window.location.href = `${window.location.protocol}//${window.location.host}/`;

          return;
        }

        // Handle login callback
        const hasAccessTokenInHash = window.location.hash.includes("access_token");
        const hasIdTokenInHash = window.location.hash.includes("id_token");

        if (hasAccessTokenInHash && hasIdTokenInHash) {
          const callbackResult = await authService.handleCallback();
          isCallbackHandled = callbackResult;
        }

        // Update authentication state
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);

        // Auto-redirect after successful authentication
        if (
          authenticated &&
          isCallbackHandled &&
          (window.location.pathname === "/" || window.location.pathname === "/index.html")
        ) {
          setTimeout(() => {
            const event = new CustomEvent("auth-redirect", {
              detail: "/interactions",
            });
            window.dispatchEvent(event);
          }, 100);
        }
      } catch (_error) {
        // Silently handle auth initialization errors
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (): Promise<void> => {
    await authService.login();
  };

  const logout = (): void => {
    authService.logout();
    setIsAuthenticated(false);
  };

  const getToken = (): string | null => {
    return authService.getAccessToken();
  };

  const value: AuthContextType = {
    isAuthenticated,
    login,
    logout,
    getToken,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
